import { useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';

import classNames from 'classnames';

import { modifyCompanionAd, showTag } from '../../../services/appnexus';

import * as styles from './DisplayAd.module.scss';

interface DisplayAdProps {
  targetId: string;
  onShow?: () => void;
  header?: ReactNode;
  className?: string;
  initiallyShown?: boolean;
  isLoaded?: boolean;
  isCompanion?: boolean;
  videoCreativeId?: string;
}

type AdState = 'possible' | 'visible' | 'non-visible' | 'idle';

const DisplayAd: FC<DisplayAdProps> = ({
  targetId,
  onShow,
  header,
  className,
  initiallyShown = false,
  isLoaded = false,
  isCompanion = false,
  videoCreativeId,
}) => {
  const [adState, setAdState] = useState<AdState>(
    initiallyShown ? 'possible' : 'idle'
  );

  const isShown = adState === 'possible' || adState === 'visible';

  useEffect(() => {
    (async () => {
      if (!isLoaded) {
        return;
      }

      try {
        await showTag(targetId);

        setAdState('visible');
      } catch {
        setAdState('non-visible');
      }

      if (typeof onShow === 'function') {
        onShow();
      }
    })();
  }, [isLoaded, targetId, onShow]);

  const isTagLoaded = adState === 'visible' || adState === 'non-visible';

  useEffect(() => {
    (async () => {
      if (isTagLoaded && isCompanion && videoCreativeId) {
        try {
          await modifyCompanionAd(targetId, videoCreativeId);

          setAdState('visible');

          if (typeof onShow === 'function') {
            onShow();
          }
        } catch {}
      }
    })();
  }, [isTagLoaded, isCompanion, videoCreativeId, targetId, onShow]);

  return (
    <div
      className={classNames(
        styles.displayAd,
        { [styles.isShown]: isShown },
        className
      )}
    >
      {Boolean(header) && isShown ? header : null}
      <div
        id={targetId}
        className={classNames(
          styles.adContainer,
          styles[targetId as keyof typeof styles]
        )}
      />
    </div>
  );
};

export { DisplayAd };
